import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf-worker/pdf.worker.min.js';

const RENDER_SCALE = 1.5;

const FormsBekijken = () => {
  const { id: submissionId } = useParams();
  const navigate = useNavigate();

  const [submission, setSubmission] = useState(null);
  const [pages, setPages] = useState([]);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForward, setShowForward] = useState(false);
  const [toast, setToast] = useState(null);
  // ACTIVITY_TIMELINE: audit-events voor deze submission
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    if (!submissionId) return;
    setAuditLoading(true);
    api.get(`/audit/by-target/form_submission/${submissionId}`)
      .then(r => { if (!cancelled) setAuditEvents(r.data?.events || []); })
      .catch(() => { if (!cancelled) setAuditEvents([]); })
      .finally(() => { if (!cancelled) setAuditLoading(false); });
    return () => { cancelled = true; };
  }, [submissionId]);

  const canvasRefs = useRef({});

  // Submission ophalen + PDF laden
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const subRes = await api.get(`/form-submissions/${submissionId}`);
        if (cancelled) return;
        const sub = subRes.data.submission;
        setSubmission(sub);

        if (!sub.gegenereerde_pdf_pad) {
          setError('Voor dit formulier is nog geen PDF gegenereerd.');
          setLoading(false);
          return;
        }

        const token = localStorage.getItem('token');
        const pdfRes = await fetch(`/api/form-submissions/${submissionId}/pdf`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!pdfRes.ok) throw new Error(`PDF kon niet worden geladen (HTTP ${pdfRes.status})`);
        const blob = await pdfRes.blob();
        setPdfBlob(blob);

        const buf = await blob.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        if (cancelled) return;

        const info = [];
        for (let p = 1; p <= pdf.numPages; p++) {
          const page = await pdf.getPage(p);
          const viewport = page.getViewport({ scale: RENDER_SCALE });
          info.push({ pageNum: p, width: viewport.width, height: viewport.height, page, viewport });
        }
        if (!cancelled) {
          setPages(info);
          setLoading(false);
        }
      } catch (err) {
        console.error('Bekijken laad-fout:', err);
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Fout bij laden');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [submissionId]);

  // Render pages
  useEffect(() => {
    pages.forEach(({ pageNum, page, viewport }) => {
      const canvas = canvasRefs.current[pageNum];
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      page.render({ canvasContext: ctx, viewport }).promise.catch(e =>
        console.error(`Pagina ${pageNum} render fout:`, e)
      );
    });
  }, [pages]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleDownload = useCallback(() => {
    if (!pdfBlob) return;
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = (submission?.template_naam || `submission-${submissionId}`).replace(/[^a-z0-9-_ ]/gi, '_');
    a.download = `${safeName}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [pdfBlob, submission, submissionId]);

  if (loading) return <Center>📄 PDF laden…</Center>;
  if (error)   return <Center error>{`⚠️ ${error}`}<br/><br/><button onClick={() => navigate(-1)} style={btnSecondary}>Terug</button></Center>;

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', paddingBottom: 90 }}>
      {/* Header */}
      <div style={header}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate(-1)} style={btnHeader}>← Terug</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {submission?.template_naam || 'Document'}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
              Status: {submission?.status} · #{submissionId}
            </div>
          </div>
          <button onClick={() => setShowForward(true)} style={btnHeader}>📧 Doorsturen</button>
          <button onClick={handleDownload} style={btnHeaderPrimary}>📥 Download</button>
        </div>
      </div>

      {/* PDF preview */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 20 }}>
        {pages.map(({ pageNum, width, height }) => (
          <div key={pageNum} style={{ marginBottom: 16 }}>
            <div style={pageBadge}>Pagina {pageNum}</div>
            <canvas
              ref={el => { canvasRefs.current[pageNum] = el; }}
              width={width} height={height}
              style={{
                display: 'block',
                marginTop: 4,
                boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                borderRadius: 4,
                maxWidth: '100%',
                height: 'auto',
                background: 'white'
              }}
            />
          </div>
        ))}
        {/* ACTIVITY_TIMELINE: audit-tijdlijn onder de PDF */}
        <ActivityTimeline events={auditEvents} loading={auditLoading} />
      </div>

      {/* Doorsturen modal */}
      {showForward && (
        <ForwardModal
          submissionId={submissionId}
          onClose={() => setShowForward(false)}
          onSuccess={(email) => {
            setShowForward(false);
            showToast(`✅ Magic-link verstuurd naar ${email}`);
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 70, left: 16, right: 16, zIndex: 1000,
          background: toast.type === 'error' ? '#dc2626' : '#10b981',
          color: 'white', padding: '12px 16px', borderRadius: 10, fontWeight: 600,
          textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          maxWidth: 500, margin: '0 auto'
        }}>{toast.msg}</div>
      )}
    </div>
  );
};

// ===== Doorsturen modal =====
const ForwardModal = ({ submissionId, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [naam, setNaam] = useState('');
  const [dagen, setDagen] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true); setErr(null);
    try {
      const body = {
        submission_id: parseInt(submissionId, 10),
        actie: 'bekijken',
        ontvanger_email: email.trim(),
        ontvanger_naam: naam.trim() || undefined
      };
      if (dagen) body.geldig_dagen = parseInt(dagen, 10);

      const r = await api.post('/form-submission-tokens', body);
      if (r.data?.success) {
        onSuccess(email.trim());
      } else {
        setErr(r.data?.message || 'Onbekende fout');
      }
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>Doorsturen via magic-link</h2>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
          De ontvanger kan dit document bekijken via een unieke link, zonder in te loggen.
          De link is 30 dagen geldig (standaard).
        </p>
        <label style={lab}>
          E-mail ontvanger <span style={{ color: '#dc2626' }}>*</span>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="naam@voorbeeld.nl" autoFocus style={inp} />
        </label>
        <label style={lab}>
          Naam (optioneel)
          <input type="text" value={naam} onChange={e => setNaam(e.target.value)}
            placeholder="Bijv. Jan Jansen" style={inp} />
        </label>
        <label style={lab}>
          Geldig (dagen) — leeg = 30
          <input type="number" min={1} max={365} value={dagen} onChange={e => setDagen(e.target.value)}
            placeholder="30" style={{ ...inp, width: 120 }} />
        </label>
        {err && <div style={errBox}>⚠️ {err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={btnSecondary} disabled={submitting}>Annuleren</button>
          <button
            onClick={submit}
            disabled={!valid || submitting}
            style={{ ...btnPrimary, opacity: (!valid || submitting) ? 0.4 : 1, cursor: (!valid || submitting) ? 'not-allowed' : 'pointer' }}
          >
            {submitting ? 'Versturen…' : '📧 Verstuur link'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Centered helper =====
const Center = ({ children, error }) => (
  <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
    <div style={{
      background: 'white', borderRadius: 12, padding: 24, textAlign: 'center',
      maxWidth: 400, color: error ? '#991b1b' : '#475569',
      boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
    }}>{children}</div>
  </div>
);

// ===== Styles =====
const header = { background: '#1e3a5f', position: 'sticky', top: 0, zIndex: 10 };
const btnHeader = { padding: '8px 14px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const btnHeaderPrimary = { padding: '8px 14px', background: '#10b981', border: 'none', color: 'white', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 };
const btnSecondary = { padding: '8px 14px', border: '1px solid #cbd5e1', background: 'white', borderRadius: 6, fontSize: 14, cursor: 'pointer', fontWeight: 500 };
const btnPrimary = { padding: '10px 16px', border: 'none', background: '#1e3a5f', color: 'white', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 600 };
const pageBadge = { display: 'inline-block', padding: '3px 10px', background: '#1e3a5f', color: 'white', fontSize: 11, fontWeight: 600, borderRadius: 4 };
const lab = { display: 'block', marginBottom: 12, fontWeight: 600, fontSize: 13, color: '#1e293b' };
const inp = { display: 'block', width: '100%', marginTop: 6, padding: 10, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
const errBox = { marginTop: 12, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 6, fontSize: 13 };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 };
const modalBox = { background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 20px 50px rgba(0,0,0,0.3)' };

// ===== ACTIVITY_TIMELINE: audit-events onder de PDF =====
const ActivityTimeline = ({ events, loading }) => {
  if (loading) {
    return (
      <div style={timelineWrap}>
        <div style={timelineTitle}>📋 Activiteit</div>
        <div style={{ color: '#94a3b8', fontSize: 13 }}>Laden…</div>
      </div>
    );
  }
  if (!events || events.length === 0) {
    return (
      <div style={timelineWrap}>
        <div style={timelineTitle}>📋 Activiteit</div>
        <div style={{ color: '#94a3b8', fontSize: 13 }}>Geen activiteit gevonden</div>
      </div>
    );
  }
  return (
    <div style={timelineWrap}>
      <div style={timelineTitle}>📋 Activiteit ({events.length})</div>
      <div style={{ position: 'relative', paddingLeft: 28 }}>
        <div style={{
          position: 'absolute', left: 11, top: 8, bottom: 8,
          width: 2, background: '#e2e8f0'
        }} />
        {events.map((e, idx) => {
          const meta = describeAction(e.action);
          const datum = new Date(e.created_at);
          const datumStr = datum.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
          const tijdStr = datum.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
          const actor = e.actor_naam && e.actor_naam.trim() !== ''
            ? e.actor_naam
            : (e.details?.signer_naam || 'Externe ondertekenaar');
          return (
            <div key={e.id || idx} style={{ position: 'relative', marginBottom: 14, paddingLeft: 4 }}>
              <div style={{
                position: 'absolute', left: -22, top: 2,
                width: 22, height: 22, borderRadius: '50%',
                background: meta.color, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700,
                boxShadow: '0 0 0 3px white'
              }}>{meta.icon}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                {meta.label}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                door <strong>{actor}</strong> · {datumStr} {tijdStr}
              </div>
              {e.details?.reden && (
                <div style={{ fontSize: 12, color: '#991b1b', marginTop: 4, fontStyle: 'italic' }}>
                  Reden: {e.details.reden}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

function describeAction(action) {
  const map = {
    'form.submission.created':   { label: 'Submission aangemaakt',    icon: '+', color: '#3b82f6' },
    'form.submission.updated':   { label: 'Concept aangepast',        icon: '✎', color: '#6b7280' },
    'form.submission.submitted': { label: 'Ingediend',                icon: '✓', color: '#10b981' },
    'form.submission.deleted':   { label: 'Verwijderd',               icon: '×', color: '#dc2626' },
    'form.submission.emailed':   { label: 'Per email verstuurd',      icon: '✉', color: '#8b5cf6' },
    'form.quicksign.created':    { label: 'Quick-Sign aangemaakt',    icon: '+', color: '#3b82f6' },
    'form.signer.signed':        { label: 'Getekend',                 icon: '✍', color: '#10b981' },
    'form.signer.refused':       { label: 'Geweigerd',                icon: '✗', color: '#dc2626' },
    'form.workflow.completed':   { label: 'Workflow compleet',        icon: '★', color: '#059669' },
  };
  return map[action] || { label: action, icon: '•', color: '#64748b' };
}

const timelineWrap = {
  marginTop: 24,
  background: 'white',
  borderRadius: 8,
  padding: 20,
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
};
const timelineTitle = {
  fontSize: 15, fontWeight: 700, color: '#1e293b',
  marginBottom: 16
};

export default FormsBekijken;
